import { useState, type ReactNode } from "react";
import { Navigate, Outlet, useNavigate } from "react-router";
import { AppShell, Button, Card, Dialog, Stack } from "@/components";
import { FranjaVerComo } from "./FranjaVerComo";
import type { Fideicomiso, Rol, Usuario } from "@/lib/api";
import { guardarDesarrolloElegido, leerDesarrolloElegido, membresiaActual, RUTA_PERFIL } from "@/lib/navegacion";
import { NuevoDesarrollo } from "@/tabs/desarrollos/NuevoDesarrollo";
import { useContextoApp, type ContextoApp } from "./contexto";
import { useMenu } from "./useMenu";

export interface PrivadoProps {
  usuario: Usuario;
  onSalir: () => void;
  /** Vuelve a pedir /api/auth/yo (después de crear un desarrollo, para que aparezca la membresía nueva). */
  onRecargarUsuario: () => Promise<void>;
}

/** Layout de las rutas logueadas: resuelve el desarrollo actual (selector + navegador) y lo pasa a las pantallas. */
export function Privado({ usuario, onSalir, onRecargarUsuario }: PrivadoProps) {
  const navigate = useNavigate();
  const [elegido, setElegido] = useState<number | null>(leerDesarrolloElegido);
  const [creando, setCreando] = useState(false);
  const membresia = membresiaActual(usuario, elegido);
  // Sin membresía el rol no importa: el hook no pide nada para quien no es admin.
  const menu = useMenu(membresia?.rol ?? "escribano", membresia?.fideicomiso_id ?? 0);

  const elegir = (fid: number) => {
    guardarDesarrolloElegido(fid);
    setElegido(fid);
  };
  const cambiarDesarrollo = (fid: number) => {
    elegir(fid);
    navigate("/");
  };
  const alCrear = async (creado: Fideicomiso) => {
    elegir(creado.id);
    await onRecargarUsuario();
    setCreando(false);
    navigate("/configuracion/fideicomiso");
  };

  if (!membresia) {
    return (
      <div className="grid min-h-dvh place-items-center p-4">
        <Card className="w-full max-w-sm">
          <Stack espacio={3}>
            {usuario.puede_crear_desarrollo ? (
              <>
                <h1 className="text-pantalla font-light tracking-[-0.03em]">Creá tu primer desarrollo</h1>
                <p className="text-texto-suave">Todavía no hay ningún desarrollo cargado. Empezá por el nombre; el resto se completa después.</p>
                <NuevoDesarrollo alCrear={alCrear} />
              </>
            ) : (
              <p>Tu usuario todavía no tiene acceso a ningún desarrollo. Pedile acceso a un administrador.</p>
            )}
            <div>
              <Button variante="secundario" onClick={onSalir}>
                Salir
              </Button>
            </div>
          </Stack>
        </Card>
      </div>
    );
  }
  const contexto: ContextoApp = {
    usuario,
    membresia,
    fid: membresia.fideicomiso_id,
    puestaEnMarcha: menu.puesta,
    recargarUsuario: onRecargarUsuario,
  };
  return (
    <AppShell
      titulo={membresia.fideicomiso_nombre}
      nombreUsuario={usuario.nombre}
      grupos={menu.grupos}
      gruposUsuario={menu.gruposUsuario}
      rutaPerfil={RUTA_PERFIL}
      onSalir={onSalir}
      desarrollos={usuario.membresias.map((m) => ({ id: m.fideicomiso_id, nombre: m.fideicomiso_nombre }))}
      desarrolloActual={membresia.fideicomiso_id}
      onCambiarDesarrollo={cambiarDesarrollo}
      onNuevoDesarrollo={usuario.puede_crear_desarrollo ? () => setCreando(true) : undefined}
    >
      {usuario.vista && <FranjaVerComo fid={membresia.fideicomiso_id} vista={usuario.vista} onRecargarUsuario={onRecargarUsuario} />}
      {/* Al cambiar de fiduciante en la vista, la pantalla se vuelve a montar y pide sus datos como el nuevo. */}
      <Outlet key={usuario.vista?.fiduciante_id ?? "propio"} context={contexto} />
      <Dialog abierto={creando} onCambio={setCreando} titulo="Nuevo desarrollo">
        <NuevoDesarrollo alCrear={alCrear} onCancelar={() => setCreando(false)} />
      </Dialog>
    </AppShell>
  );
}

/** Envuelve una pantalla de admin: cualquier otro rol vuelve al inicio. */
export function SoloAdmin({ children }: { children: ReactNode }) {
  const { membresia } = useContextoApp();
  return membresia.rol === "admin" ? <>{children}</> : <Navigate to="/" replace />;
}

/** Envuelve una pantalla que sólo ven ciertos roles (lib/navegacion, `rolesDe`). El resto vuelve al inicio, o a `redirigir[rol]` si está. */
export function ConRol({ roles, redirigir, children }: { roles: readonly Rol[]; redirigir?: Partial<Record<Rol, string>>; children: ReactNode }) {
  const { membresia } = useContextoApp();
  if (roles.includes(membresia.rol)) return <>{children}</>;
  return <Navigate to={redirigir?.[membresia.rol] ?? "/"} replace />;
}
